import { DataGrid } from '@mui/x-data-grid';
import { Typography, Box, Grid } from '@mui/material';
import { RedTheme } from 'common/global';
import { tablePaginationClasses } from '@mui/material';
import clsx from 'clsx';
import { useScreen } from 'common/hooks/useScreen';
import { IOSSwitch } from 'common/components/switch';
import { ThemeProvider } from '@emotion/react';
import { createTheme } from '@mui/material/styles';
import { SelectedTheme, BlueTheme } from 'common/global';
import { ReactComponent as Edit } from '../assets/images/edit.svg';
import { ReactComponent as Delete } from '../assets/images/delete.svg';
import { useDispatch, useSelector } from 'react-redux';
import { ToolTip } from 'common/components';
import {
  EditAlerts,
  MyTeamsPerformaceGetAlerts,
  EditAdminAlerts,
} from 'TAP/redux/actions/alertAction';
import { Fragment, useEffect, useState } from 'react';
import {
  MyTeamsPerformaceMuteAlerts,
  AdminViewGetAllerts,
  AdminMuteAlerts,
} from 'TAP/redux/actions/alertAction';
import { Toaster } from './Toaster';
import { labels, NUMBER_ARRAY } from 'common/global/constants';
import { ReactComponent as AlertIcon } from 'common/assets/images/AlertTosterIcon.svg';

export const ViewAlerts = ({ editHandler, deleteData, isAdmin }) => {
  const alerts = useSelector((state) => state?.alertRd);
  const alertData = alerts?.alerts?.data || [];
  const muteAlertAction = alerts?.muteAlerts?.data;
  const editedAlert = alerts?.editedAlert?.data;
  const editedAdminAlerts = alerts?.adminEditedAlerts?.data;
  const deleteAlert = alerts?.deleteAlert?.data;
  const muteAdminAlerts = alerts?.adminMuteAlerts?.data;
  const deleteAdminAlerts = alerts?.adminDeletedAlerts?.data;
  const newAlert = alerts?.createAlert?.data;
  const newAdminAlert = alerts?.createAdminAlert?.data;
  const adminAlerts = alerts?.adminAlerts?.data || [];
  const [isEditState, setIsEditState] = useState(false);
  const dispatch = useDispatch();
  let redtheme = RedTheme;
  const [allAlerts, setAllAlerts] = useState([]);
  const [pageState, setPageState] = useState({
    isLoading: false,
    page: NUMBER_ARRAY.one,
    pageSize: NUMBER_ARRAY.five,
  });
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const boxButtonStyle = (isDisabled) => ({
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    '&:hover': isDisabled ? {} : { backgroundColor: redtheme.palette.box.lightPink },
    padding: '8px',
    borderRadius: '4px',
    opacity: isDisabled ? 0.5 : 1,
    pointerEvents: isDisabled ? 'none' : 'auto',
  });

  const buttonStyle = {
    color: 'inherit',
    '&:hover': {
      '& svg': {
        stroke: redtheme.palette.button.pure_red,
      },
    },
  };

  const onSuccessToasterClose = () => {
    setShowSuccessMsg(false);
  };

  useEffect(() => {
    if (isAdmin && deleteAdminAlerts) {
      dispatch(AdminViewGetAllerts());
    }
  }, [deleteAdminAlerts]);

  useEffect(() => {
    if (deleteAlert) {
      dispatch(MyTeamsPerformaceGetAlerts());
    }
  }, [deleteAlert]);

  useEffect(() => {
    if (muteAlertAction) {
      dispatch(MyTeamsPerformaceGetAlerts());
    }
  }, [muteAlertAction]);

  useEffect(() => {
    if (muteAdminAlerts) {
      dispatch(AdminViewGetAllerts());
    }
  }, [muteAdminAlerts]);

  useEffect(() => {
    if (newAlert) {
      dispatch(MyTeamsPerformaceGetAlerts());
    }
  }, [newAlert]);

  useEffect(() => {
    if (newAdminAlert) {
      dispatch(AdminViewGetAllerts());
    }
  }, [newAdminAlert]);

  useEffect(() => {
    if (editedAlert && isEditState) {
      editHandler(editedAlert);
      setIsEditState(!isEditState);
    }
  }, [editedAlert]);

  useEffect(() => {
    if (editedAdminAlerts && isEditState) {
      editHandler(editedAdminAlerts);
      setIsEditState(!isEditState);
    }
  }, [editedAdminAlerts]);

  useEffect(() => {
    const selectedAlerts = isAdmin ? adminAlerts : alertData;
    const transformedData = Array.isArray(selectedAlerts)
      ? selectedAlerts.map((alert) => {
          const userDataArray = alert.toUserData ? JSON.parse(alert.toUserData) : [];
          const fullNames = userDataArray.map((user) => user.full_name).join(', ');
          let operations = [];
          if (alert.operations) {
            operations = JSON.parse(alert.operations);
          }
          const formattedThreshold =
            operations.length === NUMBER_ARRAY.one
              ? `${operations[NUMBER_ARRAY.zero].logicalOperator} ${
                  operations[NUMBER_ARRAY.zero].threshold
                }`
              : operations
                  .map((op, index) => {
                    const nextOp = operations[index + NUMBER_ARRAY.one];
                    return nextOp ? `${op.threshold}${op.logicalOperator}` : op.threshold;
                  })
                  .join(' ');

          return {
            id: alert.Id,
            alert_name: alert.Name,
            audience: fullNames,
            threshold: formattedThreshold,
            alert_type: alert.Type,
            isActive: alert.IsActive,
          };
        })
      : [];
    setAllAlerts(transformedData);
  }, [
    adminAlerts,
    alertData,
    isAdmin,
    pageState,
    deleteAdminAlerts,
    newAdminAlert,
    deleteAlert,
    newAlert,
    muteAdminAlerts,
    muteAlertAction,
  ]);

  const totalRows = allAlerts.length;
  const [newPaginatedData, setNewPaginatedData] = useState([]);

  useEffect(() => {
    const startIndex = (pageState.page - NUMBER_ARRAY.one) * pageState.pageSize;
    const endIndex = startIndex + pageState.pageSize;
    let paginatedData = allAlerts.slice(startIndex, endIndex);
    setNewPaginatedData(paginatedData);
  }, [pageState, allAlerts]);

  const handleEditClick = (recordId) => {
    if (isAdmin) {
      dispatch(EditAdminAlerts(recordId));
    } else {
      dispatch(EditAlerts(recordId));
    }
    setIsEditState(true);
  };

  const handleDeleteClick = (rowId) => {
    deleteData(rowId);
  };

  const screen = useScreen();

  const theme = SelectedTheme();
  let complementTheme = theme.palette.type === 'red' ? BlueTheme : RedTheme;

  const switchTheme = createTheme({
    components: {
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            color: RedTheme.palette.button.white,
          },
          colorPrimary: {
            '&.Mui-checked': {
              color: complementTheme.palette.primary.main,
            },
          },
          track: {
            backgroundColor: '#C1BBB3',
            '.Mui-checked.Mui-checked + &': {
              backgroundColor: '#888B8D',
            },
          },
        },
      },
    },
  });

  const columns = [
    {
      field: 'alert_name',
      width: screen.resolveStyles({
        mobile: NUMBER_ARRAY.oneHundredEighty,
        tablet: NUMBER_ARRAY.oneHundredEighty,
        desktop: null,
      }),
      renderHeader: () => (
        <Box>
          <Typography
            sx={{ fontFamily: '"JohnsonText-Bold"', color: redtheme.palette.text.secondary }}
          >
            Alert Name
          </Typography>
        </Box>
      ),
      flex: !screen.isDesktop ? NUMBER_ARRAY.zero : NUMBER_ARRAY.one,
      disableColumnMenu: true,
      sortable: true,
      cellClassName: (params) => {
        if (params.value == null) {
          return '';
        }
        return clsx('super-app', {
          overdue: ['complete overdue'].includes(params.value.toLowerCase()),
        });
      },
      renderCell: (params) => {
        const alertName = params.value || '';
        return (
          <ToolTip
            title={alertName}
            header='Alert Name'
            placement='top'
            noOfLetters={NUMBER_ARRAY.twentyFive}
          />
        );
      },
    },
    ...(isAdmin
      ? [
          {
            width: screen.resolveStyles({
              mobile: NUMBER_ARRAY.twoHundredTwenty,
              tablet: NUMBER_ARRAY.twoHundredTwenty,
              desktop: null,
            }),
            field: 'audience',
            renderHeader: () => (
              <Box>
                <Typography sx={{ fontFamily: '"JohnsonText-Bold"' }}>Audience</Typography>
              </Box>
            ),
            flex: !screen.isDesktop ? NUMBER_ARRAY.zero : NUMBER_ARRAY.two,
            disableColumnMenu: true,
            renderCell: (params) => {
              const audience = params.value || '';
              return (
                <ToolTip
                  title={audience}
                  header='Audience'
                  placement='top'
                  noOfLetters={NUMBER_ARRAY.twentyFive}
                />
              );
            },
          },
        ]
      : []),
    {
      field: 'threshold',
      width: screen.resolveStyles({
        mobile: NUMBER_ARRAY.oneHundredEighty,
        tablet: NUMBER_ARRAY.oneHundredEighty,
        desktop: null,
      }),
      renderHeader: () => (
        <Box>
          <Typography sx={{ fontFamily: '"JohnsonText-Bold"' }}>Threshold</Typography>
        </Box>
      ),
      flex: !screen.isDesktop ? NUMBER_ARRAY.zero : NUMBER_ARRAY.one,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => <Box>{params.value}</Box>,
    },
    {
      field: 'alert_type',
      width: screen.resolveStyles({
        mobile: NUMBER_ARRAY.oneHundredEighty,
        tablet: NUMBER_ARRAY.oneHundredEighty,
        desktop: null,
      }),
      renderHeader: () => (
        <Box>
          <Typography sx={{ fontFamily: '"JohnsonText-Bold"' }}>Alert Type</Typography>
        </Box>
      ),
      flex: !screen.isDesktop ? NUMBER_ARRAY.zero : NUMBER_ARRAY.one,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {params.value.charAt(NUMBER_ARRAY.zero).toUpperCase() +
            params.value.slice(NUMBER_ARRAY.one)}
        </Box>
      ),
    },
    {
      field: '',
      width: screen.resolveStyles({
        mobile: NUMBER_ARRAY.oneHundredEighty,
        tablet: NUMBER_ARRAY.oneHundredEighty,
        desktop: null,
      }),
      renderHeader: () => (
        <Box>
          <Typography sx={{ fontFamily: '"JohnsonText-Bold"' }}>Actions</Typography>
        </Box>
      ),
      flex: !screen.isDesktop ? NUMBER_ARRAY.zero : NUMBER_ARRAY.one,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isStandardAlert = params.row.alert_type === 'standard';
        const isAlertActive = params.row.isActive === NUMBER_ARRAY.zero;
        return (
          <Box display='flex' flexDirection='column' sx={{ fontFamily: '"JohnsonText-Regular"' }}>
            <Grid container spacing={NUMBER_ARRAY.zero}>
              <Fragment>
                <Grid item xs={NUMBER_ARRAY.six}>
                  <Box
                    sx={boxButtonStyle(isStandardAlert || isAlertActive)}
                    onClick={() => {
                      if (isStandardAlert || isAlertActive) return;
                      handleEditClick(params.row.id);
                    }}
                  >
                    <Edit data-testid='edit-icon' style={buttonStyle} disabled={isStandardAlert} />
                  </Box>
                </Grid>
                <Grid item xs={NUMBER_ARRAY.six}>
                  <Box
                    sx={boxButtonStyle(isStandardAlert)}
                    onClick={() => {
                      if (isStandardAlert) return;
                      handleDeleteClick(params.row.id);
                    }}
                  >
                    <Delete
                      data-testid='delete-icon'
                      style={buttonStyle}
                      disabled={isStandardAlert}
                    />
                  </Box>
                </Grid>
              </Fragment>
            </Grid>
          </Box>
        );
      },
    },
    {
      field: 'mute',
      width: screen.resolveStyles({
        mobile: NUMBER_ARRAY.oneHundredEighty,
        tablet: NUMBER_ARRAY.oneHundredEighty,
        desktop: null,
      }),
      sortable: false,
      renderHeader: () => <Box></Box>,
      flex: !screen.isDesktop ? NUMBER_ARRAY.zero : NUMBER_ARRAY.one,
      disableColumnMenu: true,

      renderCell: (params) => {
        const [isMuted, setIsMuted] = useState(!params.row.isActive);

        useEffect(() => {
          if (muteAlertAction) {
            const alertData = muteAlertAction.find((alert) => alert.Id === params.row.id);
            if (alertData) {
              setIsMuted(!alertData.IsActive);
            }
          }
        }, [muteAlertAction, params.row.id, params.row.isActive]);

        useEffect(() => {
          if (muteAdminAlerts) {
            const adminAlertData = muteAdminAlerts.find((alert) => alert.Id === params.row.id);
            if (adminAlertData) {
              setIsMuted(!adminAlertData.IsActive);
            }
          }
        }, [muteAdminAlerts, params.row.id, params.row.isActive]);

        useEffect(() => {
          if (isMuted) {
            setSuccessMessage(labels.muteAlertSuccessMessage);
          } else {
            setSuccessMessage(labels.unMuteAlertSuccessMessage);
          }
          if (isMuted !== !params.row.isActive) {
            setShowSuccessMsg(true);
          }
        }, [isMuted, params.row.isActive]);

        const handleMuteChange = (e, id) => {
          e.stopPropagation();
          const newMuteState = !isMuted;
          setIsMuted(newMuteState);
          const isMuteUnmute = newMuteState ? NUMBER_ARRAY.zero : NUMBER_ARRAY.one;
          if (isAdmin) {
            dispatch(AdminMuteAlerts({}, id, isMuteUnmute));
          } else {
            dispatch(MyTeamsPerformaceMuteAlerts({}, id, isMuteUnmute));
          }
        };
        return (
          <Box display='flex' flexDirection='column' sx={{ fontFamily: '"JohnsonText-Regular"' }}>
            <Grid container>
              <Grid item xs={NUMBER_ARRAY.eight}>
                <Typography
                  sx={{
                    fontSize: '16px',
                    lineHeight: '14px',
                    fontWeight: NUMBER_ARRAY.fiveHundred,
                    fontFamily: '"JohnsonText-Bold"',
                    color: 'rgba(33, 33, 33, 1)',
                    height: '16px',
                    paddingTop: '3px',
                  }}
                >
                  Mute
                </Typography>
              </Grid>
              <Grid item xs={NUMBER_ARRAY.four}>
                <Box>
                  <ThemeProvider theme={switchTheme}>
                    <IOSSwitch
                      id='statusSwitch'
                      onChange={(e) => handleMuteChange(e, params.row.id)}
                      data-testid='switch-button'
                      checked={isMuted}
                    />
                  </ThemeProvider>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ height: '70%' }}>
      <Toaster
        onClose={onSuccessToasterClose}
        hideDuration={NUMBER_ARRAY.fiveThousand}
        severity={'success'}
        show={showSuccessMsg}
        message={successMessage}
        isAlertPopup={true}
        isIconPresent={true}
        icon={<AlertIcon />}
      />
      <DataGrid
        disableVirtualization={process.env.NODE_ENV === 'test'} // virtualization disabled only during tests - generating all columns during tests
        initialState={{
          sorting: {
            sortModel: [{ field: 'alert_type', sort: 'desc' }],
          },
          pagination: {
            paginationModel: { pageSize: NUMBER_ARRAY.five },
          },
        }}
        rows={newPaginatedData}
        columns={columns}
        getRowId={(row) => row.id}
        sortingMode='client'
        paginationMode='server'
        disableRowSelectionOnClick
        pageSizeOptions={[NUMBER_ARRAY.five, NUMBER_ARRAY.ten, NUMBER_ARRAY.fifteen]}
        pageSize={pageState.pageSize}
        loading={pageState.isLoading}
        pagination
        onPageSizeChange={(newPageSize) =>
          setPageState((old) => ({ ...old, pageSize: newPageSize }))
        }
        onPaginationModelChange={(newPage) => {
          setPageState((old) => ({
            ...old,
            page: newPage.page + NUMBER_ARRAY.one,
            pageSize: newPage.pageSize,
          }));
        }}
        rowCount={totalRows}
        sx={{
          border: 'none',
          height: '90%',
          marginTop: '50px',
          '& .MuiDataGrid-cell': {
            fontSize: '14px',
            fontWeight: 450,
            fontFamily: '"J&J CircularTT Book Web"',
          },
          '& .MuiDataGrid-row': {
            '&:hover': {
              backgroundColor: redtheme.palette.dataGrid.hoverGrey,
            },
          },
          '& .MuiDataGrid-row:nth-of-type(odd)': {
            backgroundColor: 'rgba(0, 0, 153, 0.03)',
          },
          '& .MuiDataGrid-row:nth-of-type(odd):hover': {
            backgroundColor: redtheme.palette.dataGrid.very_light_grey,
          },
          '& .MuiDataGrid-columnHeaders': {
            fontSize: 16,
            fontFamily: '"J&J CircularTT Black Web"',
          },
          '& .MuiDataGrid-row.Mui-selected': { backgroundColor: redtheme.palette.dataGrid.white },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: redtheme.palette.dataGrid.white,
          },
          '& .MuiDataGrid-row.Mui-selected:nth-of-type(odd)': {
            backgroundColor: 'rgba(0, 0, 153, 0.03)',
          },
          '& .MuiDataGrid-row.Mui-selected:nth-of-type(odd):hover': {
            backgroundColor: redtheme.palette.dataGrid.very_light_grey,
          },
          '& .MuiDataGrid-rowCount': {
            fontWeight: 'bold',
          },
          '& .MuiTablePagination-selectLabel': {
            fontFamily: '"J&J CircularTT Black Web"',
            fontSize: '16px',
            fontWeight: '700',
            lineHeight: '18px',
            fontStyle: 'normal',
            color: redtheme.palette.dataGrid.fontGrey,
          },
          '& .MuiTablePagination-displayedRows': {
            fontFamily: '"J&J CircularTT Black Web"',
            fontSize: '16px',
            fontWeight: '700',
            lineHeight: '18px',
            fontStyle: 'normal',
            color: redtheme.palette.dataGrid.fontGrey,
          },
          '& .MuiTablePagination-select': {
            fontFamily: '"J&J CircularTT Black Web"',
            fontSize: '16px',
            fontWeight: '700',
            lineHeight: '26px',
            fontStyle: 'normal',
            color: redtheme.palette.dataGrid.fontGrey,
          },
          '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
          '&.MuiDataGrid-root .MuiDataGrid-cellContent': {
            pointerEvents: 'none',
            fontWeight: '400',
            fontSize: '1rem',
          },
          [`& .${tablePaginationClasses.selectLabel}`]: {
            display: 'block',
          },
          [`& .${tablePaginationClasses.input}`]: {
            display: 'inline-flex',
          },
        }}
      />
    </Box>
  );
};
